import { useContext, useEffect, useState } from "react";

import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import api from "../../services/api";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import { CheckCircle as CheckCircleIcon, Send as SendIcon, SwapHoriz as SwapHorizIcon } from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import { EditMessageProvider } from "../../context/EditingMessage/EditingMessageContext";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { BotReplyProvider } from "../../context/BotReply/BotReplyContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import MessagesList from "../MessagesList";
import NewTicketModalWecloud from "../NewTicketModalWecloud";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "70vh",
    flexDirection: "column",
    position: "relative",
    overflow: "hidden",
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },

  messagesWrapper: {
    flex: 1,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },

  inputWrapper: {
    borderTop: "1px solid #1A84FF",
    padding: theme.spacing(2),
    backgroundColor: "#E9F3FF",
    boxShadow: "0 -2px 8px rgba(26, 132, 255, 0.1)",
  },

  messageInput: {
    backgroundColor: "#ffffff",
    borderRadius: theme.spacing(1),
    border: "2px solid #1A84FF",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#ffffff",
      "& fieldset": {
        borderColor: "#1A84FF",
        borderWidth: "2px",
      },
      "&:hover fieldset": {
        borderColor: "#046AE0",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#046AE0",
        borderWidth: "2px",
      },
    },
    "& .MuiInputBase-input": {
      color: "#333333",
      fontWeight: "500",
      fontSize: "14px",
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#7B7B7B",
      opacity: 1,
    },
  },

  sendButton: {
    marginLeft: theme.spacing(1),
  },

  messageBox: {
    display: "flex",
    alignItems: "flex-end",
    gap: theme.spacing(1),
  },

  inputLabel: {
    marginBottom: theme.spacing(1),
    fontWeight: "600",
    fontSize: "14px",
    color: "#1A84FF",
    textShadow: "0 1px 2px rgba(0,0,0,0.1)",
  },
}));

export default function TicketMessagesDialog({ open, handleClose, ticketId }) {
  const history = useHistory();
  const classes = useStyles();

  const { user } = useContext(AuthContext);

  const [, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [message, setMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [requestingTransfer, setRequestingTransfer] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState(null);

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    let delayDebounceFn = null;
    if (open) {
      setLoading(true);
      delayDebounceFn = setTimeout(() => {
        const fetchTicket = async () => {
          try {
            const { data } = await api.get("/tickets/" + ticketId);
            const { queueId, status, userId: ticketUserId } = data;
            const { queues, profile } = user;

            const queueAllowed = queues.find((q) => q.id === queueId);
            const isPendingUnassigned = status === "pending" && !ticketUserId;
            // Permitir acesso a admin, super ou usuários gestores (mesmo sem estar na fila)
            if (
              !isPendingUnassigned &&
              queueAllowed === undefined &&
              profile !== "admin" &&
              profile !== "super"
            ) {
              // Para usuários comuns, ainda verificar se tem permissão
              // Mas para gestores, permitir visualização e envio de mensagens
              if (!["admin", "super"].includes(profile)) {
                toast.error("Acesso não permitido");
                history.push("/tickets");
                return;
              }
            }

            setContact(data.contact);
            setTicket(data);
            setLoading(false);
          } catch (err) {
            setLoading(false);
            toastError(err);
          }
        };
        fetchTicket();
      }, 500);
    }
    return () => {
      if (delayDebounceFn !== null) {
        clearTimeout(delayDebounceFn);
      }
    };
  }, [ticketId, user, history, open]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    let socket = null;
    let onReturn = () => { };

    if (open) {
      socket = socketManager.GetSocket(companyId);

      const onConnectTicketMessagesDialog = () => {
        socket.emit("joinChatBox", `${ticket.id}`);
      }

      const onCompanyTicketMessagesDialog = (data) => {
        if (data.action === "update" && data.ticket.id === ticket.id) {
          setTicket(data.ticket);
        }

        if (data.action === "delete" && data.ticketId === ticket.id) {
          history.push("/tickets");
        }
      }

      const onCompanyContactMessagesDialog = (data) => {
        if (data.action === "update") {
          setContact((prevState) => {
            if (prevState.id === data.contact?.id) {
              return { ...prevState, ...data.contact };
            }
            return prevState;
          });
        }
      }

      onReturn = () => {
        if (socket !== null) {
          socket.disconnect();
        }
      }

      socketManager.onConnect(onConnectTicketMessagesDialog);
      socket.on(`company-${companyId}-ticket`, onCompanyTicketMessagesDialog);
      socket.on(`company-${companyId}-contact`, onCompanyContactMessagesDialog);
    }

    return onReturn;
  }, [ticketId, ticket, history, open, socketManager]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleSendMessage = async () => {
    if (!message.trim() || sendingMessage) return;

    setSendingMessage(true);

    try {
      const messageData = {
        read: 1,
        fromMe: true,
        mediaUrl: '',
        body: `*${user?.name}:*\n${message.trim()}`,
        quotedMsg: null,
        internal: false
      };

      await api.post(`/messages/${ticketId}`, messageData);
      setMessage('');
      toast.success('Mensagem enviada com sucesso!');
    } catch (err) {
      toastError(err);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleAcceptTicket = async () => {
    if (accepting) return;

    setAccepting(true);
    try {
      const { data } = await api.put(`/tickets/${ticket.id}`, {
        status: 'open',
        userId: user?.id
      });
      setTicket(data);
      toast.success('Atendimento aceito com sucesso!');
    } catch (err) {
      toastError(err);
    } finally {
      setAccepting(false);
    }
  };

  const handleRequestTransfer = async () => {
    if (requestingTransfer) return;

    setRequestingTransfer(true);
    try {
      await api.post(`/tickets/${ticket.id}/request-transfer`, {
        preferredTargetQueueId: ticket.queueId || undefined
      });
      toast.success('Solicitação de transferência enviada com sucesso!');
    } catch (err) {
      toastError(err);
    } finally {
      setRequestingTransfer(false);
    }
  };

  const handleOpenNewTicketForContact = (contactData) => {
    setContactTicket(contactData);
    setNewTicketModalOpen(true);
  };

  const handleCloseOrOpenTicket = (createdTicket) => {
    setNewTicketModalOpen(false);
    setContactTicket(null);

    if (createdTicket?.uuid) {
      history.push(`/tickets/${createdTicket.uuid}`);
      return;
    }

    if (createdTicket?.id) {
      history.push(`/tickets/${createdTicket.id}`);
    }
  };

  const renderTicketInfo = () => {
    if (ticket.user !== undefined) {
      return (
        <TicketInfo
          contact={contact}
          ticket={ticket}
          onClick={handleDrawerOpen}
        />
      );
    }
  };

  const renderMessagesList = () => {
    return (
      <Box className={classes.root}>
        <Box className={classes.messagesWrapper}>
          <MessagesList
            ticket={ticket}
            ticketId={ticket.id}
            isGroup={ticket.isGroup}
            onOpenNewTicketForContact={handleOpenNewTicketForContact}
          />
        </Box>
        <Box className={classes.inputWrapper}>
          {ticket.status === 'pending' && !ticket.isGroup ? (
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleAcceptTicket}
              disabled={accepting}
              startIcon={<CheckCircleIcon />}
            >
              {accepting ? 'Aceitando...' : 'Aceitar Atendimento'}
            </Button>
          ) : ticket.status === 'open' &&
            !ticket.isGroup &&
            ticket.userId &&
            Number(ticket.userId) !== Number(user?.id) ? (
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleRequestTransfer}
              disabled={requestingTransfer}
              startIcon={<SwapHorizIcon />}
            >
              {requestingTransfer
                ? 'Solicitando...'
                : 'Solicitar Transferência'}
            </Button>
          ) : (
            <>
              <Typography className={classes.inputLabel}>
                Enviar mensagem como {user?.name}:
              </Typography>
              <Box className={classes.messageBox}>
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  variant="outlined"
                  placeholder="Digite sua mensagem..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={sendingMessage}
                  className={classes.messageInput}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSendMessage}
                  disabled={!message.trim() || sendingMessage}
                  className={classes.sendButton}
                  startIcon={<SendIcon />}
                >
                  {sendingMessage ? 'Enviando...' : 'Enviar'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog maxWidth="lg" fullWidth onClose={handleClose} open={open}>
      <TicketHeader loading={loading}>{renderTicketInfo()}</TicketHeader>
      <ReplyMessageProvider>
        <EditMessageProvider>
          <BotReplyProvider>
            {renderMessagesList()}
          </BotReplyProvider>
        </EditMessageProvider>
      </ReplyMessageProvider>
      <NewTicketModalWecloud
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={handleCloseOrOpenTicket}
      />
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
